/**
 * Présence Teams — libellé, pastille et ordre d'affichage.
 *
 * SOURCE : le couple (availability, activity) renvoyé par Graph. `activity` est la valeur FINE
 * (`InACall`, `Presenting`, `BeRightBack`…), `availability` la valeur grossière (`Available`,
 * `Busy`, `Away`, `DoNotDisturb`, `Offline`). Le dashboard temps réel n'affichait que la seconde :
 * un agent en communication ressortait « Busy », comme un agent simplement occupé dans son
 * agenda, alors que la console agent, elle, affiche « InACall ». Les deux écrans décrivaient le
 * même agent avec deux mots différents.
 *
 * Le tableau ci-dessous reprend celui de la console agent
 * (AgentConsole/src/components/Header/HeaderComponent.js — `presenceMapping`) : mêmes couples,
 * mêmes libellés, mêmes pastilles. Toute divergence future se corrige ici ET là-bas.
 */

/** Pastille Fluent par disponibilité. L'activité n'y change rien : c'est la couleur « joignable ». */
const BADGE_BY_AVAILABILITY = {
    Available: 'available',
    AvailableIdle: 'available',
    Busy: 'busy',
    BusyIdle: 'busy',
    DoNotDisturb: 'do-not-disturb',
    Away: 'away',
    BeRightBack: 'away',
    Offline: 'offline',
    PresenceUnknown: 'unknown',
};

/**
 * Rang d'affichage demandé : d'abord qui peut prendre un appel, puis qui est en ligne, puis les
 * absents, et les déconnectés en dernier — un superviseur lit la liste de haut en bas et doit
 * trouver en tête ce sur quoi il peut agir.
 */
export const PRESENCE_RANK = {
    AVAILABLE: 0,
    IN_CALL: 1,
    AWAY: 2,
    OFFLINE: 3,
};

const IN_CALL_ACTIVITIES = new Set([
    'InACall', 'InAConferenceCall', 'InAMeeting', 'Presenting', 'UrgentInterruptionsOnly',
]);

/** Normalise ce que renvoient Graph, la file ou un agent hors ligne. */
const pick = (presence, fallbackState) => ({
    availability: presence?.availability || '',
    activity: presence?.activity || '',
    fallback: fallbackState || '',
});

/**
 * Libellé affiché sous le nom de l'agent.
 * Priorité à `activity` : c'est elle qui distingue « en communication » de « occupé ».
 * À défaut de présence Teams, on retombe sur l'état porté par la file (reason code).
 */
export const presenceLabel = (presence, fallbackState, t) => {
    const { availability, activity, fallback } = pick(presence, fallbackState);
    const key = activity || availability || fallback;
    if (!key) return t ? t('REALTIME.PRESENCE.Unknown', { defaultValue: 'Unknown' }) : 'Unknown';
    // defaultValue : une activité Teams non traduite s'affiche telle quelle plutôt qu'en clé brute.
    return t ? t(`REALTIME.PRESENCE.${key}`, { defaultValue: key }) : key;
};

/** Statut de pastille Fluent (available / busy / away / do-not-disturb / offline / unknown). */
export const presenceBadge = (presence, fallbackState) => {
    const { availability, activity, fallback } = pick(presence, fallbackState);
    if (availability && BADGE_BY_AVAILABILITY[availability]) return BADGE_BY_AVAILABILITY[availability];

    // Pas de présence Teams : on interprète l'état de file. « READY » = joignable, le reste non.
    const raw = String(activity || fallback).toLowerCase();
    if (!raw) return 'offline';
    if (raw.includes('ready') || raw.includes('available')) return 'available';
    if (raw.includes('call') || raw.includes('busy')) return 'busy';
    if (raw.includes('offline') || raw.includes('shift')) return 'offline';
    return 'away';
};

/** Rang de tri (cf. PRESENCE_RANK). */
export const presenceRank = (presence, fallbackState) => {
    const { availability, activity, fallback } = pick(presence, fallbackState);

    if (IN_CALL_ACTIVITIES.has(activity)) return PRESENCE_RANK.IN_CALL;

    switch (availability) {
        case 'Available':
        case 'AvailableIdle':
            return PRESENCE_RANK.AVAILABLE;
        case 'Busy':
        case 'BusyIdle':
        case 'DoNotDisturb':
            // Occupé sans être en appel (réunion bloquée dans l'agenda, mode ne pas déranger) :
            // l'agent ne prendra pas d'appel, il appartient au groupe des indisponibles.
            return PRESENCE_RANK.AWAY;
        case 'Away':
        case 'BeRightBack':
            return PRESENCE_RANK.AWAY;
        case 'Offline':
        case 'PresenceUnknown':
            return PRESENCE_RANK.OFFLINE;
        default:
            break;
    }

    // Aucune présence Teams : l'état de file décide.
    const raw = String(fallback).toLowerCase();
    if (!raw) return PRESENCE_RANK.OFFLINE;
    if (raw.includes('ready') || raw.includes('available')) return PRESENCE_RANK.AVAILABLE;
    if (raw.includes('call')) return PRESENCE_RANK.IN_CALL;
    if (raw.includes('offline') || raw.includes('shift')) return PRESENCE_RANK.OFFLINE;
    return PRESENCE_RANK.AWAY;
};

/**
 * Ordonne une liste d'agents : disponibles, en appel, absents, déconnectés — puis par nom à
 * l'intérieur de chaque groupe, pour qu'un agent ne saute pas de place à chaque rafraîchissement
 * de présence (toutes les 5 s) alors que rien n'a changé pour lui.
 */
export const sortAgentsByPresence = (agents = [], presenceOf = () => null) =>
    [...agents].sort((a, b) => {
        const rankDiff = presenceRank(presenceOf(a), a?.state || a?.status)
            - presenceRank(presenceOf(b), b?.state || b?.status);
        if (rankDiff !== 0) return rankDiff;
        const nameA = a?.name || a?.displayName || a?.email || '';
        const nameB = b?.name || b?.displayName || b?.email || '';
        return nameA.localeCompare(nameB);
    });
